/**
 * Curfew report grid — relay off-command log for a customer within a date range.
 *
 * Server-backed via EntityGrid over odl-app/curfew-report/. Columns come from the
 * backend grid-spec descriptor. Date range drives filter[start_date]/filter[end_date]
 * via extraParams. The toolbar hosts a TimeRangeSelector. Excel export calls the
 * legacy /customers/{id}/curfewreport/ endpoint with the current date range and sort,
 * building the file client-side via getExcelFile.
 *
 * Parity note: the new odl-app endpoint returns all RELAY_STATE_OFF/RELAY_OFF
 * commands in the date range (annotated with curfew_start_time/curfew_end_time),
 * without the legacy per-row timezone-aware curfew-window filter. The result set
 * may be slightly wider than the legacy grid — rows outside the curfew window are
 * now included. This is accepted; a client-side post-filter is deferred.
 *
 * Contains:
 * - CurfewReportGrid — EntityGrid over odl-app/curfew-report/ (default export)
 */

import { CheckCircleOutlineOutlined, CircleOutlined, FileDownload } from '@mui/icons-material';
import { Box, CircularProgress } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { endOfDay, format, startOfDay, subMonths } from 'date-fns';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { fetchCurfewReportPage } from '@sophocles/api/reports';
import TimeRangeSelector from '@sophocles/components/TimeRangeSelector';
import { EntityGrid } from '@sophocles/data-grids';
import { ToolbarIconButton } from '@sophocles/data-grids/components/toolbar';
import { getExcelFile } from '@sophocles/utils';
import { buildCurfewReportParams } from '@sophocles/views/reports/curfewReportParams';

function CurfewReportGrid({ customerId }) {
  const { t } = useTranslation();

  const [startDate, setStartDate] = useState(() => startOfDay(subMonths(new Date(), 1)));
  const [endDate, setEndDate] = useState(() => endOfDay(new Date()));
  const [exporting, setExporting] = useState(false);

  // Shared apiRef so the export handler can read the current sort model.
  const apiRef = useGridApiRef();

  // extraParams drives the EntityGrid list fetch. Each key becomes filter[key] on the wire.
  const extraParams = useMemo(
    () => buildCurfewReportParams({ customerId, startDate, endDate }),
    [customerId, startDate, endDate],
  );

  const handleDateRangeChange = useCallback((start, end) => {
    setStartDate(start);
    setEndDate(end);
  }, []);

  // Excel export: fetches from the legacy /customers/{id}/curfewreport/ endpoint
  // with the current date range and sort, then builds the file client-side.
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const sortModel = apiRef.current?.getSortModel() ?? [];
      const sortParam =
        sortModel.length > 0
          ? sortModel[0].sort === 'desc'
            ? `-${sortModel[0].field}`
            : sortModel[0].field
          : '-timestamp';

      let all = [];
      let offset = 0;
      const limit = 500;
      const startStr = format(startDate, 'yyyy-MM-dd');
      const endStr = format(endDate, 'yyyy-MM-dd');

      while (true) {
        const data = await fetchCurfewReportPage(customerId, {
          start_date: startStr,
          end_date: endStr,
          o: sortParam,
          limit,
          offset,
        });
        const results = data?.results || [];
        all = [...all, ...results];
        if (!data?.next) break;
        offset += limit;
      }

      const rows = all.map(r => ({
        plant_name: `${r.plant_name ?? ''}${r.plant_description ? ` - ${r.plant_description}` : ''}`,
        site_name: r.site_name ?? '',
        timestamp: r.timestamp ? moment(r.timestamp).format('YYYY-MM-DD HH:mm:ss') : '',
        curfew: `${r.curfew_start_time ?? ''} - ${r.curfew_end_time ?? ''}`,
        user_name: r.user_name ?? '',
        unit_command_issued: r.unit_command_issued ?? '',
        relay: r.relay ?? '',
        initiated_by_system: r.initiated_by_system ? t('general.yes') : t('general.no'),
      }));

      const headers = [
        [t('reports.curfewReport')],
        [`${startStr} to ${endStr}`],
        [],
        [
          t('reports.columnSiteGroup', 'Site Group'),
          t('general.site'),
          t('general.timestamp'),
          t('reports.curfew', 'Curfew'),
          t('general.user'),
          t('reports.command', 'Command'),
          t('reports.relay', 'Relay'),
          t('reports.system', 'System'),
        ],
      ];
      const dataColumns = [
        'plant_name',
        'site_name',
        'timestamp',
        'curfew',
        'user_name',
        'unit_command_issued',
        'relay',
        'initiated_by_system',
      ];
      getExcelFile(rows, dataColumns, headers, `curfew-report-${Date.now()}`);
    } finally {
      setExporting(false);
    }
  }, [customerId, extraParams, startDate, endDate, apiRef, t]);

  // Render overrides on descriptor columns — sizing/order/headers come from the descriptor.
  const fieldOverrides = useMemo(
    () => ({
      plant_name: {
        renderCell: ({ row, value }) => (
          <Link to={`/c/${row.customer_id}/plant/${row.plant_id}/structures`}>
            {value}
            {row.plant_description ? ` - ${row.plant_description}` : ''}
          </Link>
        ),
      },
      site_name: {
        renderCell: ({ row, value }) => (
          <Link to={`/c/${row.customer_id}/plant/${row.plant_id}/structure/${row.site_id}`}>{value}</Link>
        ),
      },
      timestamp: {
        valueFormatter: value => (value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : ''),
      },
      // Combined curfew window: "HH:MM - HH:MM" from the annotated start/end fields.
      curfew_start_time: {
        renderCell: ({ row, value }) => `${value ?? ''} - ${row.curfew_end_time ?? ''}`,
      },
      initiated_by_system: {
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ value }) =>
          value ? <CheckCircleOutlineOutlined fontSize="small" /> : <CircleOutlined fontSize="small" />,
      },
    }),
    [],
  );

  const toolbarContent = (
    <TimeRangeSelector
      startDate={startDate}
      endDate={endDate}
      onChange={handleDateRangeChange}
      disabled={exporting}
      size="sm"
      defaultPreset="last30d"
    />
  );

  const exportButton = (
    <ToolbarIconButton
      tooltip={t('general.export', { defaultValue: 'Export' })}
      onClick={handleExport}
      disabled={exporting}
    >
      {exporting ? <CircularProgress size={18} /> : <FileDownload sx={{ fontSize: 18 }} />}
    </ToolbarIconButton>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <EntityGrid
        basePath="curfew-report"
        fieldOverrides={fieldOverrides}
        extraParams={extraParams}
        options={{
          gridApiRef: apiRef,
          defaultSort: [{ field: 'timestamp', sort: 'desc' }],
          persistKey: 'curfewReportGrid',
          toolbarContent,
          toolbarActions: exportButton,
          toolbarVariant: 'flat',
          countNoun: t('reports.commands', 'commands'),
          gridProps: {
            density: 'compact',
            disableRowSelectionOnClick: true,
          },
        }}
      />
    </Box>
  );
}

export default CurfewReportGrid;
